<template>
  <q-dialog v-model="shown">
    <q-card style="width: 450px;">
      <q-card-section class="row items-center q-mx-xs q-gutter-xs full-width no-wrap">
        <div class="text-h6 col ellipsis">
          Edit {{ $_.startCase(field) }} Filter
        </div>
        <q-space />
        <q-btn
          class="col-auto"
          dense
          outline
          label="Reset"
          size="sm"
          @click="internalList = []; excluding = false"
        />
        <q-toggle
          v-model="excluding"
          class="col-auto"
          keep-color
          :color="excluding ? 'red' : 'green'"
          unchecked-icon="check"
          checked-icon="clear"
          :label="excluding ? 'Exclude' : 'Include'"
          @click.stop
        />
        <q-btn
          v-close-popup
          class="col-auto"
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section v-if="error">
        <div class="text-red q-pa-xl">
          Error: {{ errorMessage }}
        </div>
      </q-card-section>
      <q-card-section
        v-else-if="loading"
        class="q-pa-xl"
      >
        <q-spinner
          class="fixed-center"
          size="30px"
        />
      </q-card-section>
      <q-card-section v-else>
        <q-input
          v-model.trim="optionsSearchString"
          debounce="200"
          style="margin-bottom: 15px"
          :placeholder="`Search ${title}...`"
          clearable
          dense
        />
        <div
          class="justify-between q-mt-sm"
        >
          <transition-group
            name="list-complete"
          >
            <div
              v-for="item in matchingOptions"
              :key="item.id || item.value || item.key || item"
              style="margin-bottom: -1px"
              class="list-complete-item"
            >
              <q-item
                style="border: solid 1px #CCC;"
                :class="$_.includes(internalList, item) && (excluding ? 'bg-red-1' : 'bg-green-1')"
              >
                <slot :item="item">
                  <q-item-section>
                    {{ item.label || $_.startCase(item.value || item) }}
                  </q-item-section>
                </slot>
                <q-item-section
                  class="col-auto"
                  style="border: solid 1px #ddd;"
                >
                  <q-checkbox
                    :model-value="$_.includes(internalList, item)"
                    :label="excluding ? 'Exclude' : 'Include'"
                    class="q-mr-md q-ml-xs"
                    @update:model-value="toggle(item)"
                  />
                </q-item-section>
              </q-item>
            </div>
          </transition-group>
          <div
            v-if="optionsSearchString && matchingOptions.length == 0"
            class="q-pa-lg text-center"
          >
            No Matching Searches
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script>

import Report, { SYSTEM_REPORTS } from 'src/models/report/Report'

export default {
  name: 'ReportEditFilterDialog',
  components: {
  },
  props: {
    title: {
      type: String,
      required: false,
      default: 'Values'
    },
    modelValue: {
      type: Boolean,
      required: false
    },
    field: {
      type: String,
      required: false,
      default: null
    },
    report: {
      type: Object,
      required: true
    },
    reportElement: {
      type: Object,
      required: true
    }
  },
  emits: ['update:model-value', 'preview-changes'],
  data () {
    return {
      shown: false,

      loading: false,
      error: false,
      errorMessage: null,

      options: [],

      excluding: false,
      internalList: [],
      optionsSearchString: null
    }
  },
  computed: {
    combinedList () {
      // Union with all options so selected values are first
      return _.union(this.internalList, this.options)
    },
    matchingOptions () {
      if (!this.optionsSearchString) return this.combinedList

      const searchString = this.optionsSearchString.toLowerCase()
      return _.filter(
        this.combinedList,
        option =>
          _.includes(option.toLowerCase(), searchString)
      )
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.error = false
      this.errorMessage = null

      if (!_.includes(this.reportElement.reportOptions.group_by, this.field)) {
        this.loadOptions()
      } else {
        this.options = this.reportElement.possibleOptionsValues[`${this.field}_filter`] || []
      }

      this.internalList = this.reportElement.reportOptions[`${this.field}_filter`] || []
      if (_.isString(this.internalList)) {
        this.internalList = this.internalList.split(',')
      }
      this.initializing = true
      if (this.reportElement.reportOptions[`exclude_${this.field}`] === null) {
        this.reportElement.reportOptions[`exclude_${this.field}`] = false
      }
      this.excluding = this.reportElement.reportOptions[`exclude_${this.field}`]
      this.initializing = false

      this.$emit('update:model-value', this.shown)
    },
    excluding () {
      if (this.initializing) return

      this.reportElement.reportOptions[`exclude_${this.field}`] = this.excluding

      if (this.internalList.length) {
        this.internalList = _.without(this.options, ...this.internalList)
      }

      this.$emit('preview-changes')
    },
    internalList () {
      this.reportElement.reportOptions[`${this.field}_filter`] = this.internalList
      this.$emit('preview-changes')
    }
  },
  configuration: ['currentCircuit'],
  methods: {
    show () {
      this.shown = true
    },
    toggle (item) {
      if (item === false) {
        item = 'false'
      } else if (item === true) {
        item = 'true'
      }
      if (_.includes(this.internalList, item)) {
        this.internalList = _.without(this.internalList, item)
      } else {
        this.internalList.push(item)
      }
    },
    async loadOptions (loadAll = true) {
      this.options = []
      this.loading = true

      let reportElement
      if (this.reportElement === this.report) {
        // Find the first report element that has this filterable column
        reportElement = _.find(this.reportElement.elements.data, element => {
          return _.find(element.filterableColumns, { field: this.field })
        }) || this.reportElement.elements.data[0]
      } else {
        reportElement = this.reportElement
      }

      const reportOptions = {
        ...reportElement.finalOptions,
        group_by: [this.field]
      }
      reportElement.resetAllFilters(reportOptions)

      // For performance reasons, only load the first day of data
      if (!loadAll) {
        reportOptions.end_date = reportOptions.start_date
      }

      this.optionsLoaderReport = new Report({
        id: this.currentCircuit.reportIdForName(SYSTEM_REPORTS.OPTIONS_LOADER_REPORT),
        reportOptions
      }, {
        reportStatus: true,
        elements: {
          possibleOptionsValues: true
        }
      })
      await this.optionsLoaderReport.runReport()

      this.checkReportStatus()
    },
    checkReportStatus () {
      if (!this.optionsLoaderReport.reportStatus.done && !this.optionsLoaderReport.reportStatus.error) {
        setTimeout(() => {
          this.checkReportStatus()
        }, 100)

        return
      }

      if (this.optionsLoaderReport.reportStatus.done) {
        this.loading = false
        this.options = this.optionsLoaderReport.elements.data[0].possibleOptionsValues[`${this.field}_filter`] || []
      } else if (this.optionsLoaderReport.reportStatus.error) {
        this.error = true
        this.errorMessage = this.optionsLoaderReport.reportStatus.errorMessage
        this.loading = false
      }

      this.optionsLoaderReport.destroy()
      this.optionsLoaderReport = null
    }
  }
}

</script>

<style lang="scss" scoped>
.list-complete-item {
  transition: all 0.5s;
}
.list-complete-enter {
  opacity: 0;
  transform: translateX(0) translateY(0);
}
.list-complete-leave-to {
  opacity: 0;
  transform: translateX(0) translateY(0);
}
.list-complete-leave-active {
  position: absolute;
}
</style>
